import { useRef, useState } from 'react';
import { ApiError } from '../../utils/api-error';
import ModerationDialog from './ModerationDialog';
import { downloadScsPanelReport } from '../OverviewScreen/scs-panel-report';
import type { ModerationPeriod, ModerationRow } from './service';

export default function ModerationReportDownload({
  rows,
  period,
  year,
  token,
  disabled,
  onGenerating,
}: {
  rows: ModerationRow[];
  period: ModerationPeriod;
  year: string;
  token: () => Promise<string>;
  disabled: boolean;
  onGenerating: (value: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const [reportYear, setReportYear] = useState(year);
  const [reportQuarter, setReportQuarter] = useState<ModerationPeriod | ''>(period);
  const [grade, setGrade] = useState('');
  const [error, setError] = useState('');
  const busy = useRef(false);
  const button = useRef<HTMLButtonElement>(null);
  const years = [...new Set(rows.map((row) => row.reportYear))].filter(Boolean).sort().reverse();
  const quarters = (['Q2', 'Q4'] as const).filter((quarter) =>
    rows.some((row) => row.reportYear === reportYear && row.period === quarter),
  );
  const matches = rows.filter(
    (row) =>
      row.period === reportQuarter &&
      row.reportYear === reportYear &&
      (!grade || row.grade.replace(/\s/g, '') === grade),
  );
  const download = async () => {
    if (busy.current || !matches.length) return;
    busy.current = true;
    setOpen(false);
    setError('');
    onGenerating(true);
    try {
      await downloadScsPanelReport(
        await token(),
        matches.map((row) => row.formId),
        reportYear,
        `Quarter ${reportQuarter.slice(-1)}`,
        grade,
      );
      requestAnimationFrame(() => button.current?.focus());
    } catch (cause) {
      setError(
        cause instanceof ApiError
          ? cause.detail || cause.message
          : cause instanceof Error
            ? cause.message
            : 'Unable to download moderation report.',
      );
      setOpen(true);
    } finally {
      busy.current = false;
      onGenerating(false);
    }
  };
  return (
    <>
      <button
        ref={button}
        type="button"
        className="govuk-button govuk-button--secondary"
        disabled={disabled || busy.current}
        onClick={() => {
          const periodYears = years.filter((value) =>
            rows.some((row) => row.period === period && row.reportYear === value),
          );
          setReportYear(periodYears.includes(year) ? year : periodYears[0] || '');
          setReportQuarter(period);
          setGrade('');
          setError('');
          setOpen(true);
        }}
      >
        Download Moderation Report
      </button>
      {open && (
        <ModerationDialog title="Download Moderation Report" onClose={() => setOpen(false)}>
          <form
            className="scs-moderation-dialog__content"
            onSubmit={(event) => {
              event.preventDefault();
              void download();
            }}
          >
            <p className="govuk-body">
              Choose the reporting period and quarter to include in the report.
            </p>
            {error && (
              <p className="govuk-error-message" role="alert">
                {error}
              </p>
            )}
            <div className="govuk-form-group">
              <label className="govuk-label" htmlFor="moderation-report-year">
                Reporting period
              </label>
              <select
                className="govuk-select"
                id="moderation-report-year"
                value={reportYear}
                onChange={(event) => {
                  const value = event.target.value;
                  setReportYear(value);
                  if (
                    !rows.some((row) => row.reportYear === value && row.period === reportQuarter)
                  ) {
                    setReportQuarter('');
                  }
                }}
                disabled={!years.length}
              >
                {years.map((value) => (
                  <option key={value}>{value}</option>
                ))}
              </select>
            </div>
            <div className="govuk-form-group">
              <label className="govuk-label" htmlFor="moderation-report-quarter">
                Reporting Quarter
              </label>
              <select
                className="govuk-select"
                id="moderation-report-quarter"
                value={reportQuarter}
                onChange={(event) => setReportQuarter(event.target.value as ModerationPeriod | '')}
                disabled={!quarters.length}
              >
                <option value="">Select quarter</option>
                {quarters.map((value) => (
                  <option key={value} value={value}>
                    Quarter {value.slice(-1)}
                  </option>
                ))}
              </select>
            </div>
            <div className="govuk-form-group">
              <label className="govuk-label" htmlFor="moderation-report-grade">
                Substantive Grade
              </label>
              <select
                className="govuk-select"
                id="moderation-report-grade"
                value={grade}
                onChange={(event) => setGrade(event.target.value)}
              >
                <option value="">All Grades</option>
                {['SCS1', 'SCS2', 'SCS3', 'SCS4'].map((value) => (
                  <option key={value}>{value}</option>
                ))}
              </select>
            </div>
            {!matches.length && (
              <p className="govuk-body" role="status">
                No responses match these filters.
              </p>
            )}
            <button type="submit" className="govuk-button" disabled={!matches.length}>
              Download
            </button>{' '}
            <button
              type="button"
              className="govuk-button govuk-button--secondary"
              onClick={() => setOpen(false)}
            >
              Cancel
            </button>
          </form>
        </ModerationDialog>
      )}
    </>
  );
}
